import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp, ArrowRight, ArrowDown, CalendarIcon, Search, User, Building2, Handshake, X, ChevronDown, Check } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { people, companies, deals, workspaceMembers } from "@/data/dummy";
import CompanyLogo from "@/components/shared/CompanyLogo";
import UserAvatar from "@/components/shared/Avatar";
import NoteEditor from "@/components/shared/NoteEditor";
import type { PartialBlock } from "@blocknote/core";
import { useTaskMutations } from "@/lib/hooks/use-task-mutations";
import {
  NotePriorityEnum,
  useNotesSubjectSearchLazyQuery,
  Account,
  Contact,
  Deal,
} from "@/lib/graphql/types-and-hooks";
import { isLovablePreview } from "@/lib/utils/environment";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useWorkspaceMembers, type WorkspaceMember } from "@/lib/hooks/use-workspace-members";
import { useUser } from "@/contexts/UserContext";
import { displayDate } from "@/lib/i18n/display";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSubjects?: AssocSubject[];
  onAdd?: (task: {
    title: string;
    description: string;
    priority: string;
    dueDate: string;
    associationType: string;
    associationName: string;
  }) => void;
}

export type { AssocSubject };

type TaskSubject = Account | Contact | Deal;

type AssocSubject = {
  id: string;
  type: "person" | "company" | "deal";
  name: string;
  sub?: string;
};

/**
 * `value` is the stored priority (mapped onto NotePriorityEnum below), so it
 * stays English; `labelKey` is what the reader sees.
 */
const priorities = [
  { value: "High", labelKey: "common.high", icon: ArrowUp, color: "text-rose-600" },
  { value: "Medium", labelKey: "common.medium", icon: ArrowRight, color: "text-orange-500" },
  { value: "Low", labelKey: "common.low", icon: ArrowDown, color: "text-amber-500" },
];

/**
 * date-fns pattern, not copy. Hoisted out of JSX so the i18n lint rule does not
 * read the token string as user-facing text; the app-wide move onto the shared
 * locale-aware formatters is tracked separately.
 */
const DATE_PATTERN = "MMM d, yyyy";

export default function AddTaskDialog({ open, onOpenChange, onAdd, initialSubjects }: Props) {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const { createTask } = useTaskMutations();
  const { currentUser } = useUser();
  const { members: backendMembers } = useWorkspaceMembers();

  // Build members list: backend members or dummy fallback
  const membersList = useMemo(() => {
    if (!isLovablePreview() && backendMembers.length > 0) {
      return backendMembers;
    }
    // Dummy fallback
    return workspaceMembers.map(m => ({
      id: m.id,
      name: m.name,
      email: m.email,
      lastLogin: m.lastLogin,
      lastLoginTs: m.lastLoginTs,
      role: m.role.toLowerCase() as 'owner' | 'admin' | 'member',
      profileImageUrl: null,
    }));
  }, [backendMembers]);

  // Default owner to current user
  const defaultOwnerId = useMemo(() => {
    if (currentUser?.id) {
      const match = membersList.find(m => m.email === currentUser.email);
      if (match) return match.id;
    }
    // Fallback to first member (for preview mode, "Doug Smith")
    return membersList[0]?.id || "";
  }, [currentUser, membersList]);

  const [title, setTitle] = useState("");
  const [descriptionBlocks, setDescriptionBlocks] = useState<PartialBlock[]>([{ type: "paragraph" }]);
  const [priority, setPriority] = useState("Medium");
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [ownerId, setOwnerId] = useState(defaultOwnerId);
  const [ownerSearchOpen, setOwnerSearchOpen] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [subjects, setSubjects] = useState<AssocSubject[]>(initialSubjects || []);
  const [backendSubjects, setBackendSubjects] = useState<TaskSubject[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, { data: searchData, loading: searchLoading }] = useNotesSubjectSearchLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const ownerDropdownRef = useRef<HTMLDivElement>(null);

  // Sync initialSubjects when dialog opens. Seed backendSubjects too — production
  // task creation derives contact/account/deal ids from backendSubjects, not the
  // display-only subjects state, so a pre-filled association won't persist without this.
  useEffect(() => {
    if (open && initialSubjects?.length) {
      setSubjects(initialSubjects);
      setBackendSubjects(initialSubjects.map(s => ({
        __typename: s.type === "person" ? "Contact" : s.type === "company" ? "Account" : "Deal",
        id: s.id,
      })) as TaskSubject[]);
    }
  }, [open, initialSubjects]);

  // Sync owner when dialog opens or defaultOwnerId changes
  useEffect(() => {
    if (open) setOwnerId(defaultOwnerId);
  }, [open, defaultOwnerId]);

  // Close owner dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ownerDropdownRef.current && !ownerDropdownRef.current.contains(event.target as Node)) {
        setOwnerSearchOpen(false);
      }
    };
    if (ownerSearchOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ownerSearchOpen]);

  const [searchValue, setSearchValue] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounced backend search
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (searchValue.trim() && workspaceId && !isLovablePreview()) {
      debounceTimerRef.current = setTimeout(() => {
        searchQuery({
          variables: {
            workspaceId,
            search: searchValue.trim(),
            first: 10,
          },
        });
      }, 300);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchValue, workspaceId, searchQuery]);

  // Backend search results
  const searchContacts = searchData?.contacts?.edges?.map(e => e?.node).filter(Boolean) || [];
  const searchAccounts = searchData?.accounts?.edges?.map(e => e?.node).filter(Boolean) || [];
  const searchDeals = searchData?.deals?.edges?.map(e => e?.node).filter(Boolean) || [];
  const hasBackendResults = searchContacts.length > 0 || searchAccounts.length > 0 || searchDeals.length > 0;

  // Dummy data fallback for preview mode only
  const allItems = useMemo(() => {
    const items: AssocSubject[] = [];
    people.forEach(p => items.push({ id: p.id, type: "person", name: p.name, sub: p.company }));
    companies.forEach(c => items.push({ id: c.id, type: "company", name: c.name, sub: c.industry }));
    deals.forEach(d => items.push({ id: d.id, type: "deal", name: d.name, sub: d.company }));
    return items;
  }, []);

  const filteredItems = useMemo(() => {
    // Only use dummy data in preview mode
    if (!isLovablePreview() || !searchValue.trim() || hasBackendResults) return [];
    const q = searchValue.toLowerCase();
    return allItems
      .filter(item => item.name.toLowerCase().includes(q) && !subjects.find(s => s.id === item.id && s.type === item.type))
      .slice(0, 8);
  }, [searchValue, allItems, subjects, hasBackendResults]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    if (showSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchDropdown]);

  const handleSubjectSelected = useCallback((subject: TaskSubject | AssocSubject) => {
    // Check if already selected
    if (subjects.find(s => s.id === subject.id)) return;

    // Handle backend subjects (Contact, Account, Deal)
    if ('__typename' in subject) {
      const typedSubject = subject as TaskSubject;

      // Add to backend subjects
      let newBackendSubjects = [...backendSubjects, typedSubject];

      // Auto-add associated accounts for contacts and deals
      if (typedSubject.__typename === "Contact" || typedSubject.__typename === "Deal") {
        const entity = typedSubject as Contact | Deal;
        const associatedAccounts = entity.accounts?.edges
          ?.map(edge => edge?.node)
          .filter((account): account is Account => !!account) || [];

        associatedAccounts.forEach(account => {
          if (!newBackendSubjects.find(s => s.id === account.id)) {
            newBackendSubjects = [...newBackendSubjects, account];
          }
        });
      }

      setBackendSubjects(newBackendSubjects);

      // Convert to UI subject for display
      const uiSubject: AssocSubject = {
        id: typedSubject.id!,
        type: typedSubject.__typename === 'Contact' ? 'person'
            : typedSubject.__typename === 'Account' ? 'company'
            : 'deal',
        name: typedSubject.__typename === 'Contact'
          ? `${(typedSubject as Contact).properties?.firstName || ''} ${(typedSubject as Contact).properties?.lastName || ''}`.trim() || (typedSubject as Contact).primaryEmail || ''
          : typedSubject.__typename === 'Account'
          ? (typedSubject as Account).properties?.name || (typedSubject as Account).domain || ''
          : (typedSubject as Deal).properties?.name || '',
        sub: typedSubject.__typename === 'Contact'
          ? (typedSubject as Contact).accounts?.edges?.[0]?.node?.properties?.name || ''
          : typedSubject.__typename === 'Account'
          ? (typedSubject as Account).domain || ''
          : (typedSubject as Deal).accounts?.edges?.[0]?.node?.properties?.name || ''
      };
      setSubjects(prev => [...prev, uiSubject]);
    } else {
      // Handle dummy data subjects (preview mode)
      const assocSubject = subject as AssocSubject;
      setSubjects(prev => [...prev, assocSubject]);
    }

    setSearchValue("");
    setShowSearchDropdown(false);
  }, [subjects, backendSubjects]);

  const handleRemoveSubject = (id: string, type: string) => {
    setSubjects(prev => prev.filter(s => !(s.id === id && s.type === type)));
    setBackendSubjects(prev => prev.filter(s => s.id !== id));
  };

  const reset = () => {
    setTitle("");
    setDescriptionBlocks([{ type: "paragraph" }]);
    setPriority("Medium");
    setDueDate(undefined);
    setOwnerId(defaultOwnerId);
    setOwnerSearch("");
    setOwnerSearchOpen(false);
    setSubjects([]);
    setBackendSubjects([]);
    setSearchValue("");
    setShowSearchDropdown(false);
  };

  const handleSubmit = async () => {
    if (title.trim()) {
      // In Lovable Preview mode, use the onAdd callback
      if (isLovablePreview()) {
        onAdd?.({
          title: title.trim(),
          description: JSON.stringify(descriptionBlocks),
          priority,
          // NOT localized: TasksContext stores this string and `parseDate` reads
          // it back for sorting, so it is a value. Only the label below is localized.
          dueDate: dueDate ? format(dueDate, DATE_PATTERN) : "",
          associationType: subjects[0]?.type || "",
          associationName: subjects[0]?.name || "",
        });
        reset();
        onOpenChange(false);
        return;
      }

      // Backend integration
      setIsSubmitting(true);
      try {
        // Convert priority to enum
        const priorityEnum = priority === 'High' ? NotePriorityEnum.High
                           : priority === 'Medium' ? NotePriorityEnum.Medium
                           : NotePriorityEnum.Low;

        // Build blocks JSON (BlockNote format) - use proper BlockNote blocks from editor
        const blocks = JSON.stringify(descriptionBlocks);

        // Use backend subject IDs (not dummy data IDs!)
        // Only include association arrays if they have values (backend expects null/undefined, not empty arrays)
        const contactIds = backendSubjects.filter(s => s.__typename === 'Contact').map(s => s.id!);
        const accountIds = backendSubjects.filter(s => s.__typename === 'Account').map(s => s.id!);
        const dealIds = backendSubjects.filter(s => s.__typename === 'Deal').map(s => s.id!);

        // Build task input, only including fields with actual values
        const taskInput: Record<string, unknown> = {
          title: title.trim(),
          blocks,
          priority: priorityEnum,
        };

        // Only add optional fields if they have values
        // Assign to the owner chosen in the form (defaults to the current user).
        if (ownerId) taskInput.userId = ownerId;
        if (dueDate) taskInput.dueDate = dueDate.toISOString();
        if (contactIds.length > 0) taskInput.contactIds = contactIds;
        if (accountIds.length > 0) taskInput.accountIds = accountIds;
        if (dealIds.length > 0) taskInput.dealIds = dealIds;

        await createTask(taskInput as any);

        reset();
        onOpenChange(false);
      } catch (error) {
        console.error('Failed to create task:', error);
        // Toast error already shown in useTaskMutations
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) handleCancel(); else onOpenChange(v); }}>
      <SheetContent data-testid="add-task-dialog" expandable className="sm:max-w-[50vw] w-full overflow-y-auto p-0 flex flex-col" onOpenAutoFocus={(e) => e.preventDefault()}>
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle className="text-lg font-display">{t("dialogs.addTask")}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="task-title" className="text-sm">{t("common.title")}</Label>
            <Input
              id="task-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={t("forms.task.titlePlaceholder")}
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-sm">{t("common.description")}</Label>
            <div className="min-h-[100px] border rounded-md">
              <NoteEditor
                initialContent={descriptionBlocks}
                onChange={setDescriptionBlocks}
                autoFocus={false}
              />
            </div>
          </div>

          {/* Priority + Due Date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">{t("common.priority")}</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map(p => {
                    const Icon = p.icon;
                    return (
                      <SelectItem key={p.value} value={p.value}>
                        <span className="flex items-center gap-1.5">
                          <Icon className={cn("h-3.5 w-3.5", p.color)} />
                          {t(p.labelKey)}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">{t("forms.task.dueDate")}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dueDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? displayDate(dueDate) : t("forms.actions.pickDate")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={setDueDate}
                    disabled={{ before: new Date() }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Owner */}
          <div className="space-y-1.5">
            <Label className="text-sm">{t("forms.labels.owner")}</Label>
            <div className="relative" ref={ownerDropdownRef}>
              <button
                type="button"
                data-testid="add-task-owner"
                onClick={() => setOwnerSearchOpen(!ownerSearchOpen)}
                className="w-full flex items-center gap-2.5 rounded-md border border-input bg-card px-3 py-2 text-sm hover:bg-muted/50 transition"
              >
                {(() => {
                  const owner = membersList.find(m => m.id === ownerId);
                  return owner ? (
                    <>
                      <UserAvatar name={owner.name} src={owner.profileImageUrl} size="xs" />
                      <span className="flex-1 text-left truncate">{owner.name}</span>
                    </>
                  ) : (
                    <>
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-left text-muted-foreground">{t("forms.task.selectOwner")}</span>
                    </>
                  );
                })()}
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              </button>

              {ownerSearchOpen && (
                <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg">
                  <div className="p-2 border-b border-border">
                    <div className="flex items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1">
                      <Search className="h-3 w-3 text-muted-foreground shrink-0" />
                      <input
                        type="text"
                        value={ownerSearch}
                        onChange={(e) => setOwnerSearch(e.target.value)}
                        placeholder={t("forms.task.searchMembers")}
                        className="w-full bg-transparent text-sm outline-none"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto p-1">
                    {membersList
                      .filter(m => !ownerSearch.trim() || m.name.toLowerCase().includes(ownerSearch.toLowerCase()) || m.email.toLowerCase().includes(ownerSearch.toLowerCase()))
                      .map(member => (
                        <button
                          key={member.id}
                          type="button"
                          data-testid="add-task-owner-option"
                          onClick={() => {
                            setOwnerId(member.id);
                            setOwnerSearch("");
                            setOwnerSearchOpen(false);
                          }}
                          className="w-full flex items-center gap-2.5 p-2 rounded-md hover:bg-muted text-left text-sm"
                        >
                          <UserAvatar name={member.name} src={member.profileImageUrl} size="xs" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium">{member.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{member.email}</div>
                          </div>
                          {member.id === ownerId && <Check className="h-3.5 w-3.5 text-accent shrink-0" />}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Associate With - Unified Search */}
          <div className="space-y-1.5">
            <Label className="text-sm">{t("forms.labels.associateWith")}</Label>

            {/* Subject Pills */}
            {subjects.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {subjects.map((subject) => (
                  <div key={`${subject.type}-${subject.id}`} data-testid="add-task-subject-pill" className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-card text-xs">
                    {subject.type === "person" && <UserAvatar name={subject.name} size="xs" />}
                    {subject.type === "company" && <CompanyLogo name={subject.name} domain={subject.sub} />}
                    {subject.type === "deal" && <Handshake className="h-4 w-4 text-muted-foreground" />}
                    <span>{subject.name}</span>
                    <button
                      onClick={() => handleRemoveSubject(subject.id, subject.type)}
                      className="ml-0.5 hover:text-foreground text-muted-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Inline Search */}
            <div className="relative" ref={dropdownRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchValue}
                  onChange={e => {
                    setSearchValue(e.target.value);
                    setShowSearchDropdown(true);
                  }}
                  onFocus={() => setShowSearchDropdown(true)}
                  placeholder={t("forms.labels.searchSubjects")}
                  className="pl-9"
                />
                {searchValue && (
                  <button
                    onClick={() => {
                      setSearchValue("");
                      setShowSearchDropdown(false);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Search Dropdown */}
              {showSearchDropdown && searchValue && (
                <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                  {searchLoading && !isLovablePreview() && (
                    <div className="text-center py-8 text-muted-foreground text-xs">{t("forms.actions.searching")}</div>
                  )}

                  {!searchLoading && !hasBackendResults && filteredItems.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-xs">{t("common.noResults")}</div>
                  )}

                  {(!searchLoading || isLovablePreview()) && (hasBackendResults || filteredItems.length > 0) && (
                    <div className="p-2">
                      {/* Backend Results - Contacts */}
                      {searchContacts.map((contact) => (
                        <button
                          key={contact.id}
                          data-testid="add-task-subject-option"
                          data-subject-type="person"
                          onClick={() => {
                            handleSubjectSelected(contact as Contact);
                            setSearchValue("");
                            setShowSearchDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          <UserAvatar
                            name={`${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim()}
                            src={contact.profileImageUrl}
                            size="sm"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {`${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim() || contact.primaryEmail}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {contact.accounts?.edges?.[0]?.node?.properties?.name || contact.accounts?.edges?.[0]?.node?.domain || ''}
                            </div>
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("forms.subjectType.person")}</span>
                        </button>
                      ))}

                      {/* Backend Results - Accounts */}
                      {searchAccounts.map((account) => (
                        <button
                          key={account.id}
                          data-testid="add-task-subject-option"
                          data-subject-type="company"
                          onClick={() => {
                            handleSubjectSelected(account as Account);
                            setSearchValue("");
                            setShowSearchDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          <CompanyLogo
                            name={account.properties?.name || account.domain || ''}
                            domain={account.domain}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {account.properties?.name || account.domain}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {account.domain}
                            </div>
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("forms.subjectType.company")}</span>
                        </button>
                      ))}

                      {/* Backend Results - Deals */}
                      {searchDeals.map((deal) => (
                        <button
                          key={deal.id}
                          data-testid="add-task-subject-option"
                          data-subject-type="deal"
                          onClick={() => {
                            handleSubjectSelected(deal as Deal);
                            setSearchValue("");
                            setShowSearchDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                            <Handshake className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {deal.properties?.name || t("forms.labels.unnamedDeal")}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {deal.accounts?.edges?.[0]?.node?.properties?.name || ''}
                            </div>
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("forms.subjectType.deal")}</span>
                        </button>
                      ))}

                      {/* Dummy data fallback (for preview mode) */}
                      {filteredItems.map((item) => (
                        <button
                          key={`${item.type}-${item.id}`}
                          onClick={() => handleSubjectSelected(item)}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          {item.type === "person" && <UserAvatar name={item.name} size="sm" />}
                          {item.type === "company" && <CompanyLogo name={item.name} domain={item.sub} />}
                          {item.type === "deal" && (
                            <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                              <Handshake className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{item.name}</div>
                            {item.sub && (
                              <div className="text-xs text-muted-foreground truncate">{item.sub}</div>
                            )}
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">
                            {item.type}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-4 flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={!title.trim() || isSubmitting}>
            {isSubmitting ? t("forms.actions.adding") : t("dialogs.addTask")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
